import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Copy,
  ExternalLink,
  RefreshCcw,
  Send,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  CheckCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import {
  adminGetCreatorBillingProfile,
  adminRefreshCreatorBilling,
  adminGetStripeDashboardLink,
  adminResendOnboardingLink,
} from '../../api/creator_billing';

/**
 * Phase 42 · Creator Billing Profile — Admin dialog.
 *
 * Read-only mirror of Stripe (single source of truth) + editable fiscal
 * section. Includes the four operational buttons required by the spec:
 * "Apri Dashboard Stripe", "Aggiorna dati Stripe", "Reinvia onboarding",
 * "Copia Stripe Account ID".
 */
const KYC_TONE = {
  approved: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  pending: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  in_review: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  past_due: 'text-red-300 bg-red-500/10 border-red-500/30',
  restricted: 'text-red-300 bg-red-500/10 border-red-500/30',
};

const KV = ({ label, value, mono = false, testId }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-2 text-[13px] last:border-b-0" data-testid={testId}>
    <span className="text-muted-foreground">{label}</span>
    <span className={`text-right text-foreground ${mono ? 'font-mono text-[12px]' : ''}`}>
      {value === undefined || value === null || value === '' ? '—' : String(value)}
    </span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section
    className="rounded-2xl border border-white/5 bg-white/[0.02] p-4"
    data-testid={testId}
  >
    <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-200/80">
      {title}
    </h3>
    {children}
  </section>
);

const CreatorBillingProfileDialog = ({ open, userId, onOpenChange }) => {
  const { t } = useTranslation('billing');
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'refresh' | 'dashboard' | 'onboarding'
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const d = await adminGetCreatorBillingProfile(userId);
      setData(d);
    } catch (e) {
      toast({
        title: t('actions.refreshed_err'),
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const stripe = data?.profile?.stripe || {};
  const personal = data?.profile?.personal || {};
  const fiscal = data?.profile?.fiscal || {};
  const capabilities = stripe.capabilities || {};
  const requirements = stripe.requirements || {};

  const kycTone = KYC_TONE[stripe.kyc_status] || KYC_TONE.pending;

  const doRefresh = async () => {
    setBusy('refresh');
    try {
      const d = await adminRefreshCreatorBilling(userId);
      setData((prev) => ({ ...prev, profile: d.profile }));
      toast({ title: t('actions.refreshed_ok') });
    } catch (e) {
      toast({
        title: t('actions.refreshed_err'),
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const doOpenStripe = async () => {
    setBusy('dashboard');
    try {
      const r = await adminGetStripeDashboardLink(userId);
      if (r.url) {
        window.open(r.url, '_blank', 'noopener,noreferrer');
      } else {
        toast({ title: t('actions.dashboard_err'), variant: 'destructive' });
      }
    } catch (e) {
      toast({
        title: t('actions.dashboard_err'),
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const doResendOnboarding = async () => {
    setBusy('onboarding');
    try {
      const r = await adminResendOnboardingLink(userId);
      if (r.url) {
        // Copy the link to clipboard so the admin can paste it to the creator
        try { await navigator.clipboard.writeText(r.url); } catch (_) { /* ignore */ }
        toast({
          title: t('actions.resend_ok'),
          description: r.url,
        });
      } else {
        toast({ title: t('actions.resend_err'), variant: 'destructive' });
      }
    } catch (e) {
      toast({
        title: t('actions.resend_err'),
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const doCopyAccountId = async () => {
    if (!stripe.account_id) return;
    try {
      await navigator.clipboard.writeText(stripe.account_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) { /* ignore */ }
  };

  const hasAccount = !!stripe.account_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-3xl overflow-y-auto"
        data-testid="admin-billing-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-300" />
            {data?.user?.full_name || data?.user?.username || t('sections.stripe')}
          </DialogTitle>
          <DialogDescription>
            {data?.user?.email} · {data?.user?.role || 'user'}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-amber-300" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Test-mode notice — always visible when Stripe is in mock mode */}
            {!hasAccount && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[12.5px] text-amber-100" data-testid="billing-no-account">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('stripe_labels.no_account')}</span>
              </div>
            )}

            {/* Operational action bar */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null || !hasAccount}
                onClick={doCopyAccountId}
                data-testid="billing-btn-copy-account"
              >
                {copied ? <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                {copied ? t('actions.copied') : t('actions.copy_account_id')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null || !hasAccount}
                onClick={doRefresh}
                data-testid="billing-btn-refresh"
              >
                {busy === 'refresh' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />}
                {t('actions.refresh')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null || !hasAccount}
                onClick={doOpenStripe}
                data-testid="billing-btn-open-stripe"
              >
                {busy === 'dashboard' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1.5 h-3.5 w-3.5" />}
                {t('actions.open_stripe')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy !== null || !hasAccount}
                onClick={doResendOnboarding}
                data-testid="billing-btn-resend-onboarding"
              >
                {busy === 'onboarding' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                {t('actions.resend_onboarding')}
              </Button>
            </div>

            {/* Stripe account status */}
            <Section title={t('sections.stripe')} testId="billing-section-stripe">
              <div className="mb-3 flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${kycTone}`}>
                  KYC · {t(`kyc.${stripe.kyc_status || 'pending'}`)}
                </span>
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${stripe.payouts_enabled ? KYC_TONE.approved : KYC_TONE.pending}`}>
                  {t('stripe_labels.payouts_enabled')} · {stripe.payouts_enabled ? '✓' : '—'}
                </span>
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${stripe.charges_enabled ? KYC_TONE.approved : KYC_TONE.pending}`}>
                  {t('stripe_labels.charges_enabled')} · {stripe.charges_enabled ? '✓' : '—'}
                </span>
              </div>
              <KV label={t('stripe_labels.account_id')} value={stripe.account_id} mono testId="billing-kv-account-id" />
              <KV label={t('stripe_labels.type')} value={stripe.type} />
              <KV label={t('stripe_labels.country')} value={stripe.country} />
              <KV label={t('stripe_labels.default_currency')} value={stripe.default_currency ? String(stripe.default_currency).toUpperCase() : null} />
              <KV label={t('stripe_labels.onboarding_status')} value={stripe.onboarding_completed ? '✓' : t('stripe_labels.onboarding_incomplete')} />
              <KV label={t('stripe_labels.completed_at')} value={stripe.onboarding_completed_at ? new Date(stripe.onboarding_completed_at).toLocaleString() : null} />
              <KV label={t('stripe_labels.last_synced_at')} value={stripe.last_synced_at ? new Date(stripe.last_synced_at).toLocaleString() : null} />
            </Section>

            {/* Capabilities */}
            <Section title={t('sections.capabilities')} testId="billing-section-capabilities">
              {Object.keys(capabilities).length === 0 ? (
                <p className="text-[13px] text-muted-foreground">{t('stripe_labels.capabilities_none')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(capabilities).map(([k, v]) => (
                    <span
                      key={k}
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${v === 'active' ? KYC_TONE.approved : KYC_TONE.pending}`}
                      data-testid={`billing-capability-${k}`}
                    >
                      {k}: {v}
                    </span>
                  ))}
                </div>
              )}
            </Section>

            {/* Requirements */}
            <Section title={t('sections.requirements')} testId="billing-section-requirements">
              {(!requirements.currently_due?.length &&
                !requirements.past_due?.length &&
                !requirements.pending_verification?.length &&
                !requirements.eventually_due?.length &&
                !requirements.disabled_reason) ? (
                <p className="text-[13px] text-emerald-300">{t('stripe_labels.requirements_none')}</p>
              ) : (
                <div className="space-y-2 text-[12.5px]">
                  {requirements.disabled_reason && (
                    <div className="text-red-300">
                      <b>{t('stripe_labels.requirements_disabled_reason')}:</b> {requirements.disabled_reason}
                    </div>
                  )}
                  {['currently_due', 'past_due', 'pending_verification', 'eventually_due'].map((k) =>
                    requirements[k]?.length ? (
                      <div key={k}>
                        <b className="text-amber-200">{t(`stripe_labels.requirements_${k}`)}:</b>
                        <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                          {requirements[k].map((r, idx) => <li key={idx}>{r}</li>)}
                        </ul>
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </Section>

            {/* Personal (Stripe) */}
            <Section title={t('sections.personal')} testId="billing-section-personal">
              <KV label="Nome" value={personal.first_name} />
              <KV label="Cognome" value={personal.last_name} />
              <KV label="Email" value={personal.email} />
              <KV label="Telefono" value={personal.phone} />
              <KV label="Anno di nascita" value={personal.dob_year} />
              <KV label="Verification" value={personal.verification_status} />
              <KV label="Paese (Stripe)" value={personal.address_country} />
              <KV label="Città (Stripe)" value={personal.address_city} />
              <KV label="CAP (Stripe)" value={personal.address_postal_code} />
            </Section>

            {/* Fiscal (Nymia-side) */}
            <Section title={t('sections.fiscal')} testId="billing-section-fiscal">
              <KV label={t('fiscal_form.address_line1')} value={fiscal.address_line1} />
              <KV label={t('fiscal_form.address_line2')} value={fiscal.address_line2} />
              <KV label={t('fiscal_form.city')} value={fiscal.city} />
              <KV label={t('fiscal_form.province')} value={fiscal.province} />
              <KV label={t('fiscal_form.postal_code')} value={fiscal.postal_code} />
              <KV label={t('fiscal_form.country_iso')} value={fiscal.country_iso} />
              <KV label={t('fiscal_form.citizenship')} value={fiscal.citizenship} />
              <KV label={t('fiscal_form.codice_fiscale')} value={fiscal.codice_fiscale} mono />
              <KV label={t('fiscal_form.vat_number')} value={fiscal.vat_number} mono />
              <KV label={t('fiscal_form.business_name')} value={fiscal.business_name} />
              <KV label={t('fiscal_form.subject_type')} value={fiscal.subject_type ? t(`fiscal_form.subject_types.${fiscal.subject_type}`) : null} />
              <KV label={t('fiscal_form.fiscal_country')} value={fiscal.fiscal_country} />
              <KV
                label={t('fiscal_form.last_updated')}
                value={fiscal.updated_at ? new Date(fiscal.updated_at).toLocaleString() : t('fiscal_form.never_updated')}
              />
            </Section>

            {/* Test-mode notice at bottom */}
            <p className="text-[11px] leading-relaxed text-muted-foreground italic">
              {t('test_mode.notice')}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreatorBillingProfileDialog;
